import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CloudArrowUp, CaretDown, CaretUp, Key, DownloadSimple, Copy, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { openDownload } from "@/lib/downloadUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stamp = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("nl-BE", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const vandaag = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.toDateString() === n.toDateString();
};

// Automatische upload per filiaal: script op de filiaal-server stuurt elke nacht de bestanden door
export const AutoUploadInstellingen = () => {
  const [open, setOpen] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [paden, setPaden] = useState({});
  const [busy, setBusy] = useState("");

  const laad = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auto-upload/configs`);
      setConfigs(res.data.configs || []);
    } catch {
      toast.error("Instellingen laden mislukt");
    }
  }, []);

  useEffect(() => {
    if (open) laad();
  }, [open, laad]);

  const bewaar = async (c, velden) => {
    setBusy(c.filiaal_id);
    try {
      const res = await axios.put(`${API}/auto-upload/configs/${c.filiaal_id}`, {
        actief: c.actief, uur: c.uur, map_id: c.map_id, ...velden,
      });
      setConfigs((prev) => prev.map((p) =>
        p.filiaal_id === c.filiaal_id ? { ...p, ...res.data } : p));
      toast.success("Bewaard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bewaren mislukt");
    } finally {
      setBusy("");
    }
  };

  const nieuweSleutel = async (c) => {
    if (!window.confirm(`Nieuwe sleutel voor ${c.filiaal_naam}? Het oude script werkt dan niet meer.`)) return;
    try {
      const res = await axios.post(`${API}/auto-upload/configs/${c.filiaal_id}/nieuwe-sleutel`);
      setConfigs((prev) => prev.map((p) =>
        p.filiaal_id === c.filiaal_id ? { ...p, token: res.data.token } : p));
      toast.success("Nieuwe sleutel aangemaakt · download het script opnieuw");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mislukt");
    }
  };

  const downloadScript = (c) => {
    const pad = paden[c.filiaal_id] || "C:\\Kassa\\Export";
    openDownload(`${API}/auto-upload/script/${c.filiaal_id}?map_pad=${encodeURIComponent(pad)}`);
  };

  return (
    <div className="bg-white border border-border rounded-sm" data-testid="auto-upload-blok">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#EAF1FF]/50 transition-colors"
        data-testid="auto-upload-toggle"
      >
        <CloudArrowUp size={26} weight="fill" className="text-[#002FA7] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Automatische upload per filiaal</p>
          <p className="text-[11px] text-muted-foreground">
            De filiaal-server stuurt elke nacht zelf de bestanden door — geen handwerk meer
          </p>
        </div>
        {open ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
      </button>

      {open && (
        <div className="border-t border-border divide-y divide-border">
          {configs.length === 0 && (
            <p className="px-4 py-3 text-xs text-muted-foreground">Nog geen filialen gevonden.</p>
          )}
          {configs.map((c) => (
            <div key={c.filiaal_id} className="px-4 py-3 space-y-2" data-testid={`auto-upload-rij-${c.filiaal_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Switch
                  checked={c.actief}
                  onCheckedChange={(v) => bewaar(c, { actief: v })}
                  disabled={busy === c.filiaal_id}
                  data-testid={`auto-upload-actief-${c.filiaal_id}`}
                />
                <span className="font-bold text-sm flex-1 min-w-[120px]">{c.filiaal_naam}</span>

                <label className="text-[11px] font-semibold text-muted-foreground">Elke dag om</label>
                <Input
                  type="time"
                  value={c.uur}
                  onChange={(e) => setConfigs((prev) => prev.map((p) =>
                    p.filiaal_id === c.filiaal_id ? { ...p, uur: e.target.value } : p))}
                  onBlur={() => bewaar(c, {})}
                  className="w-28 h-9 rounded-sm"
                  data-testid={`auto-upload-uur-${c.filiaal_id}`}
                />

                {c.mappen.length > 0 && (
                  <select
                    value={c.map_id || ""}
                    onChange={(e) => bewaar(c, { map_id: e.target.value || null })}
                    className="h-9 rounded-sm border border-border text-xs px-2"
                    data-testid={`auto-upload-map-${c.filiaal_id}`}
                  >
                    <option value="">Hoofdmap van het filiaal</option>
                    {c.mappen.map((m) => (
                      <option key={m.id} value={m.id}>{m.naam}</option>
                    ))}
                  </select>
                )}

                {c.actief && (
                  vandaag(c.laatst_ontvangen) ? (
                    <Badge className="bg-emerald-500 text-white gap-1" data-testid={`auto-upload-status-${c.filiaal_id}`}>
                      <CheckCircle size={12} weight="fill" /> vandaag ontvangen
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-500 text-amber-700 gap-1" data-testid={`auto-upload-status-${c.filiaal_id}`}>
                      <WarningCircle size={12} weight="fill" /> nog niets vandaag
                    </Badge>
                  )
                )}
              </div>

              {c.token && (
                <div className="flex flex-wrap items-center gap-2 pl-1">
                  <Input
                    value={paden[c.filiaal_id] ?? "C:\\Kassa\\Export"}
                    onChange={(e) => setPaden((p) => ({ ...p, [c.filiaal_id]: e.target.value }))}
                    placeholder="Map op de filiaal-server"
                    className="h-9 rounded-sm font-mono text-xs w-64"
                    data-testid={`auto-upload-pad-${c.filiaal_id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => downloadScript(c)}
                    className="h-9 bg-[#002FA7] hover:bg-[#002FA7]/90"
                    data-testid={`auto-upload-script-${c.filiaal_id}`}
                  >
                    <DownloadSimple size={14} weight="bold" className="mr-1" />
                    Script downloaden
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard?.writeText(c.token);
                      toast.success("Sleutel gekopieerd");
                    }}
                    className="h-9"
                    data-testid={`auto-upload-copy-${c.filiaal_id}`}
                  >
                    <Copy size={14} weight="bold" className="mr-1" /> Sleutel
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => nieuweSleutel(c)}
                    className="h-9 text-destructive border-destructive/40"
                    data-testid={`auto-upload-nieuwe-sleutel-${c.filiaal_id}`}
                  >
                    <Key size={14} weight="bold" className="mr-1" /> Nieuwe sleutel
                  </Button>
                  {c.laatst_ontvangen && (
                    <span className="text-[11px] text-muted-foreground">
                      laatst: {stamp(c.laatst_ontvangen)} · {c.laatst_bestand} ({c.aantal_totaal} totaal)
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}

          <div className="px-4 py-3 bg-muted/20 text-[11px] text-muted-foreground space-y-1">
            <p className="font-bold text-foreground">Zo zet je het op de filiaal-server (eenmalig)</p>
            <p>1. Zet hierboven de juiste map en het uur, en zet de schakelaar aan.</p>
            <p>2. Klik "Script downloaden" en plaats het .ps1-bestand op die server, bv. in C:\Scripts.</p>
            <p>3. Open Windows Taakplanner → Taak maken → Dagelijks op het gekozen uur →
              Actie: <span className="font-mono">powershell.exe</span> met argumenten{" "}
              <span className="font-mono">-ExecutionPolicy Bypass -File "C:\Scripts\upload_filiaal.ps1"</span>.</p>
            <p>4. Verzonden bestanden gaan automatisch naar de submap "Verzonden" en worden nooit dubbel verstuurd.</p>
            <p>Kwam er niets binnen 30 minuten na het ingestelde uur? Dan krijg je een Telegram-melding
              met de ontbrekende filialen.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoUploadInstellingen;
